import { Button, ButtonProps, CircularProgress } from "@mui/material";
import { styled, CSSObject } from "@mui/material/styles";
import { forwardRef } from "react";

type GeneralButtonVariant =
    | "primary"
    | "secondary"
    | "outlined"
    | "danger"
    | "dangerIcon"
    | "ghost"
    | "icon"
    | "widget";

export interface GeneralButtonProps
    extends Omit<ButtonProps, "color" | "variant"> {
    appVariant?: GeneralButtonVariant;
    loading?: boolean;
    component?: React.ElementType;
    // For component="a": Button, which renders as link
    href?: string;
    target?: string;
    rel?: string;
}

const StyledButton = styled(Button, {
    shouldForwardProp: (prop) =>
        prop !== "appVariant" && prop !== "loading",
})<GeneralButtonProps>(({ theme, appVariant }): CSSObject => {
    const base: CSSObject = {
        borderRadius: 6,
        fontWeight: 600,
        textTransform: "none",
        padding: "0.5rem 1.25rem",
        minHeight: 36,
        "&.Mui-disabled": {
            opacity: 0.6,
            cursor: "not-allowed",
        },
    };

    switch (appVariant) {
        case "secondary":
            return {
                ...base,
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.grey[200],
                "&:hover": {
                    backgroundColor: theme.palette.grey[300],
                },
            };

        case "outlined":
            return {
                ...base,
                fontWeight: 500,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.grey[300]}`,
                backgroundColor: "transparent",
                "&:hover": {
                    backgroundColor: "transparent",
                    border: `1px solid ${theme.palette.grey[500]}`,
                },
            };

        case "danger":
            return {
                ...base,
                color: "#fff",
                backgroundColor: theme.palette.error.main,
                "&:hover": {
                    backgroundColor: theme.palette.error.dark,
                },
            };

        case "dangerIcon":
            return {
                ...base,
                color: theme.palette.error.main,
                backgroundColor: "#fff",
                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },
            };

        case "ghost":
            return {
                ...base,
                backgroundColor: "transparent",
                color: theme.palette.primary.main,
                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },
            };

        case "icon":
            return {
                ...base,
                padding: 4,
                minWidth: 32,
                minHeight: 32,
                borderRadius: 6,
                color: theme.palette.primary.main,
                backgroundColor: "transparent",
                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },
                "& svg": {
                    color: "currentColor",
                },
            };

        case "widget":
            return {
                ...base,
                fontSize: "0.875rem",
                height: 30,
                padding: "4px 12px",
                borderRadius: 5,
                backgroundColor: theme.palette.companyMain.main,
                color: theme.palette.text.secondary,
                border: "1px solid transparent",
                "&:hover": {
                    border: "1px solid theme.palette.companyMain.main",
                    backgroundColor: theme.palette.companyMain.hover,
                    color: theme.palette.companyMain.main,
                },
            };

        case "primary":
        default:
            return {
                ...base,
                color: "#fff",
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                },
            };
    }
});

export const GeneralButton = forwardRef<
    HTMLButtonElement,
    GeneralButtonProps
>(function GeneralButton(
    {
        children,
        loading,
        disabled,
        appVariant = "primary",
        ...props
    },
    ref
) {
    return (
        <StyledButton
            ref={ref}
            appVariant={appVariant}
            disabled={disabled || loading}
            {...props}
        >
            {loading ? (
                <CircularProgress size={20} color="inherit" />
            ) : (
                children
            )}
        </StyledButton>
    );
});

